import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel, IconSave } from '../icons/HvacIcons';
import { DEVICE_CATEGORIES, categoryInfo, iconKey, iconSvg } from './deviceCategories';
import './hv-modal.css';
import './DeviceEditModal.css';

export interface DevicePatch {
  id: string;
  plantId: string;
  symbol: string;
  name: string;
  protocol: string | null;
  firmware: string | null;
  commandCapable: boolean;
  forceCapable: boolean;
  category?: string | null;
  categoryVariant?: string | null;
  isPrimary?: boolean;
}

interface Props {
  plantId: string;
  device: {
    id: string;
    symbol: string;
    name: string;
    protocol: string | null;
    firmware: string | null;
    category: string | null;
    categoryVariant: string | null;
    isPrimary: boolean;
  };
  onSaved(patch: DevicePatch): void;
  onDeleted(deviceId: string): void;
  onClose(): void;
}

/**
 * Rename + soft-delete for a device. Symbol is immutable (anchor for the
 * deterministic device-id + MQTT topic mapping) and shown disabled; name,
 * protocol and firmware are editable free text. Delete cascades to the whole
 * sub-tree (nodes + leaves) via the server soft-delete pipeline.
 */
export function DeviceEditModal({ plantId, device, onSaved, onDeleted, onClose }: Props) {
  const [name, setName] = useState(device.name);
  const [protocol, setProtocol] = useState(device.protocol ?? '');
  const [firmware, setFirmware] = useState(device.firmware ?? '');
  const [category, setCategory] = useState<string | null>(device.category);
  const [variant, setVariant] = useState<string | null>(device.categoryVariant);
  const [isPrimary, setIsPrimary] = useState(device.isPrimary);
  const variants = categoryInfo(category)?.variants ?? [];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmed = name.trim();
      if (trimmed === '') throw new Error('Name darf nicht leer sein.');
      const body: Record<string, unknown> = { name: trimmed };
      const clear: string[] = [];
      const p = protocol.trim();
      if (p === '') clear.push('protocol'); else body.protocol = p;
      const f = firmware.trim();
      if (f === '') clear.push('firmware'); else body.firmware = f;
      // Kategorie und Auspraegung: leer heisst entfernen.
      if (category) body.category = category; else clear.push('Category');
      if (category && variant) body.categoryVariant = variant; else if (category) clear.push('CategoryVariant');
      body.isPrimary = isPrimary;
      if (clear.length > 0) body.clearFields = clear;
      const res = await api.patch<DevicePatch>(`/api/plants/${plantId}/devices/${device.id}`, body);
      onSaved(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/plants/${plantId}/devices/${device.id}`);
      onDeleted(device.id);
    } catch (e) {
      const msg = e instanceof ApiError ? `Löschen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      setError(msg);
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Device bearbeiten</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Symbol</label>
            <input type="text" value={device.symbol} disabled />
          </div>
          <div class="hv-modal__field">
            <label>Anzeigename *</label>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Protokoll</label>
              <input type="text" value={protocol} onInput={(e) => setProtocol((e.target as HTMLInputElement).value)} placeholder="z.B. bacnet-ip, modbus-tcp, opc-ua" />
            </div>
            <div class="hv-modal__field">
              <label>Firmware</label>
              <input type="text" value={firmware} onInput={(e) => setFirmware((e.target as HTMLInputElement).value)} placeholder="optional" />
            </div>
          </div>
          <div class="hv-modal__field">
            <label>Kategorie</label>
            <div class="hv-devcat" role="radiogroup" aria-label="Kategorie">
              {DEVICE_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={category === c.key}
                  class={`hv-devcat__tile${category === c.key ? ' is-active' : ''}`}
                  onClick={() => {
                    if (category === c.key) { setCategory(null); setVariant(null); return; }
                    setCategory(c.key);
                    setVariant(null);
                  }}
                  title={category === c.key ? 'Erneut klicken entfernt die Kategorie' : c.label}
                >
                  <span class="hv-devcat__icon" dangerouslySetInnerHTML={{ __html: iconSvg(iconKey(c.key), 18) }} />
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
          </div>
          {variants.length > 0 && (
            <div class="hv-modal__field">
              <label>Ausprägung</label>
              <div class="hv-devcat__seg" role="radiogroup" aria-label="Ausprägung">
                {variants.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    role="radio"
                    aria-checked={variant === v.key}
                    class={`hv-devcat__seg-btn${variant === v.key ? ' is-active' : ''}`}
                    onClick={() => setVariant(variant === v.key ? null : v.key)}
                  >
                    <span class="hv-devcat__icon" dangerouslySetInnerHTML={{ __html: iconSvg(iconKey(category, v.key), 16) }} />
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label class="hv-devcat__primary">
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary((e.currentTarget as HTMLInputElement).checked)} />
            Hauptgerät der Anlage (bestimmt das Kartensymbol)
          </label>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          {confirmDelete ? (
            <>
              <span class="hv-modal__confirm-label">Device inklusive aller Nodes und Leaves soft-löschen?</span>
              <button type="button" class="hv-btn hv-btn--danger" onClick={del} disabled={saving}>Ja, löschen</button>
              <button type="button" class="hv-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" class="hv-btn hv-btn--danger-outline" onClick={() => setConfirmDelete(true)} disabled={saving}>Löschen</button>
              <div style={{ flex: 1 }} />
              <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
              <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
